import React from 'react';
import { Link } from 'react-router-dom';

const LandingPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <h1 className="text-2xl font-bold text-gray-900">Veritas</h1>
            <div className="space-x-4">
              <Link to="/login" className="text-gray-600 hover:text-gray-900 font-medium">Sign In</Link>
              <Link to="/register" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
                Get Started
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero — the honest positioning: a CV you can defend, not auto-apply spam */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center">
          <span className="inline-block bg-blue-100 text-blue-700 text-sm font-medium px-3 py-1 rounded-full mb-5">
            Honesty-first job applications
          </span>
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            A CV you can{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">
              defend in the interview
            </span>
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            Veritas tailors your CV and cover letter to each job — then fact-checks every claim
            against your real profile, so nothing on the page can be exposed as invented.
            Search jobs across Europe, see how well you fit, and apply with confidence.
          </p>
          <div className="space-y-4 sm:space-y-0 sm:space-x-4 sm:flex sm:justify-center">
            <Link to="/register" className="inline-block bg-blue-600 text-white px-8 py-3 rounded-lg text-lg font-medium hover:bg-blue-700 transition-colors">
              Start free
            </Link>
            <Link to="/login" className="inline-block border border-gray-300 text-gray-700 px-8 py-3 rounded-lg text-lg font-medium hover:bg-gray-50 transition-colors">
              Sign In
            </Link>
          </div>
          <p className="text-sm text-gray-500 mt-4">No card required · Your data stays in the EU · You always click Submit yourself</p>
        </div>

        {/* Features — only what the product actually does */}
        <div className="mt-20 grid md:grid-cols-3 gap-8">
          <div className="text-center p-6 bg-white rounded-xl shadow-sm">
            <div className="text-4xl mb-4">✅</div>
            <h3 className="text-xl font-semibold mb-2">Truth-checked CVs</h3>
            <p className="text-gray-600">
              Every generated line is scored verifiable, inferred, or fabricated — with a
              defensibility score — so you never get caught embellishing.
            </p>
          </div>
          <div className="text-center p-6 bg-white rounded-xl shadow-sm">
            <div className="text-4xl mb-4">🔍</div>
            <h3 className="text-xl font-semibold mb-2">European job search + fit</h3>
            <p className="text-gray-600">
              Search millions of postings across 30+ countries (EURES, jobs.ch and more).
              AI ranks each by how well it matches your profile — across languages.
            </p>
          </div>
          <div className="text-center p-6 bg-white rounded-xl shadow-sm">
            <div className="text-4xl mb-4">🧩</div>
            <h3 className="text-xl font-semibold mb-2">Apply assist</h3>
            <p className="text-gray-600">
              Tailored CV and cover letter ready to download, plus a browser extension that
              prefills forms. You always review and click Submit — never automated spam.
            </p>
          </div>
        </div>

        {/* Pricing — honest tiers built on the real value (CV generation + fit analysis) */}
        <div className="mt-20 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Simple, honest pricing</h2>
          <p className="text-gray-600 mb-8">Start free. Upgrade when Veritas is landing you interviews.</p>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="p-6 bg-white rounded-xl shadow-sm border">
              <h3 className="text-xl font-semibold mb-2">Free</h3>
              <p className="text-3xl font-bold mb-4">€0<span className="text-sm text-gray-600">/month</span></p>
              <ul className="text-gray-600 mb-6 space-y-2">
                <li>5 truth-checked CVs / month</li>
                <li>Unlimited job search</li>
                <li>Fit analysis</li>
              </ul>
              <Link to="/register" className="block w-full bg-gray-100 text-gray-800 py-2 rounded-lg hover:bg-gray-200 transition-colors">
                Get Started
              </Link>
            </div>
            <div className="p-6 bg-white rounded-xl shadow-lg border-2 border-blue-500 relative">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-blue-500 text-white px-4 py-1 rounded-full text-sm">
                Popular
              </div>
              <h3 className="text-xl font-semibold mb-2">Pro</h3>
              <p className="text-3xl font-bold mb-4">€12<span className="text-sm text-gray-600">/month</span></p>
              <ul className="text-gray-600 mb-6 space-y-2">
                <li>Unlimited truth-checked CVs</li>
                <li>Deep AI fit analysis</li>
                <li>PDF & Word export</li>
                <li>Apply-assist extension</li>
              </ul>
              <Link to="/register" className="block w-full bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition-colors">
                Start Pro
              </Link>
            </div>
            <div className="p-6 bg-white rounded-xl shadow-sm border">
              <h3 className="text-xl font-semibold mb-2">Coaches & teams</h3>
              <p className="text-3xl font-bold mb-4">Custom</p>
              <ul className="text-gray-600 mb-6 space-y-2">
                <li>Career coaches & bootcamps</li>
                <li>Manage multiple candidates</li>
                <li>Branded CV templates</li>
                <li>Priority support</li>
              </ul>
              <a href="mailto:hello@veritas.app" className="block w-full bg-gray-100 text-gray-800 py-2 rounded-lg hover:bg-gray-200 transition-colors">
                Contact us
              </a>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LandingPage;
